import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { format, isValid, parse, parseISO } from "date-fns";
import { Button } from "react-bootstrap";

const formatFechaColocacionForInput = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate =
    value.includes("T")
      ? parseISO(value)
      : parse(value, "dd/MM/yyyy HH:mm", new Date());

  if (!isValid(parsedDate)) {
    return "";
  }

  return format(parsedDate, "yyyy-MM-dd");
};

const formatFechaColocacionForApi = (value) => {
  if (!value) {
    return null;
  }

  return value;
};

const MapaCamara = (props) => {
  const { camara, onSave, isSaving = false } = props;
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [fechaColocacion, setFechaColocacion] = useState("");
  const [lugar, setLugar] = useState("");

  const hasCoordinates = camara?.latitud != null && camara?.longitud != null;
  const defaultCenter = useMemo(() => [-38.950430771322864, -68.07870462416368], []);

  useEffect(() => {
    if (!camara) {
      return;
    }

    if (hasCoordinates) {
      setSelectedPoint({
        latitud: Number(camara.latitud),
        longitud: Number(camara.longitud),
      });
      return;
    }

    setSelectedPoint(null);
  }, [camara, hasCoordinates]);

  useEffect(() => {
    setFechaColocacion(formatFechaColocacionForInput(camara?.fecha_colocacion));
  }, [camara?.fecha_colocacion]);

  useEffect(() => {
    setLugar(camara?.lugar ?? "");
  }, [camara?.lugar]);

  useEffect(() => {
    setIsEditing(false);
  }, [camara?.id]);

  useEffect(() => {
    if (!camara) {
      return;
    }

    let container = L.DomUtil.get("mapaCamara");
    if (container != null) {
      container._leaflet_id = null;
    }

    const initialCenter = hasCoordinates
      ? [Number(camara.latitud), Number(camara.longitud)]
      : defaultCenter;

    const map = L.map("mapaCamara", { attributionControl: false }).setView(
      initialCenter,
      hasCoordinates ? 16 : 13
    );

    mapRef.current = map;

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      minZoom: 11,
      maxZoom: 20,
    }).addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [camara, defaultCenter, hasCoordinates]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (!selectedPoint) {
      return;
    }

    let marker;

    if (isEditing) {
      marker = L.marker([selectedPoint.latitud, selectedPoint.longitud]).addTo(map);
    } else {
      const cantidad = camara?.infracciones_count ?? 0;
      const lengthCantidad = cantidad.toString().length;
      let iconAnchor = 9;

      if (lengthCantidad > 2) {
        iconAnchor = (lengthCantidad * 11) / 2;
      } else if (lengthCantidad === 2) {
        iconAnchor = 13;
      }

      const color = cantidad === 0 ? "danger" : "success";

      marker = L.marker([selectedPoint.latitud, selectedPoint.longitud], {
        icon: L.divIcon({
          className: "custom",
          html: `
            <div class="marker" style="
              font-family: Arial, Helvetica, sans-serif;
              font-weight: 900;
              background-color: var(--${color});
              opacity: 1;
              outline: 3px solid var(--${color}-active);
              border-radius: 0.375rem;
              color: #FFF;
              padding: 2px 5px;
              display: inline-block;
              text-align: center;
              white-space: nowrap;
              max-width: 100px;
            ">
              ${cantidad}
            </div>`,
          iconSize: [0, 20],
          iconAnchor: [iconAnchor, 10],
        }),
      })
        .addTo(map)
        .bindTooltip(
          `
            <p style="font-size: .9rem; font-weight:bolder; margin-bottom: 0px;">
              Lugar: ${camara?.lugar}
            </p>`,
          {
            permanent: false,
            direction: "top",
            offset: [0, -10],
          }
        );
    }

    markerRef.current = marker;
    map.setView([selectedPoint.latitud, selectedPoint.longitud], hasCoordinates ? 16 : 15);
  }, [camara, hasCoordinates, isEditing, selectedPoint]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;

    const handleMapClick = (e) => {
      if (!isEditing) {
        return;
      }

      setSelectedPoint({
        latitud: e.latlng.lat,
        longitud: e.latlng.lng,
      });
    };

    map.on("click", handleMapClick);

    return () => {
      map.off("click", handleMapClick);
    };
  }, [isEditing]);

  const handleEnableEdition = () => {
    setIsEditing(true);
  };

  const handleCancelEdition = () => {
    setIsEditing(false);
    if (hasCoordinates) {
      setSelectedPoint({
        latitud: Number(camara.latitud),
        longitud: Number(camara.longitud),
      });
      return;
    }

    setSelectedPoint(null);
    setFechaColocacion(formatFechaColocacionForInput(camara?.fecha_colocacion));
    setLugar(camara?.lugar ?? "");
  };

  const handleSave = async () => {
    if ((!selectedPoint && !hasCoordinates) || !onSave) {
      return;
    }

    const wasSaved = await onSave({
      latitud: selectedPoint?.latitud ?? Number(camara.latitud),
      longitud: selectedPoint?.longitud ?? Number(camara.longitud),
      fecha_colocacion: formatFechaColocacionForApi(fechaColocacion),
      lugar,
    });
    if (wasSaved) {
      setIsEditing(false);
    }
  };

  return (
    <div>
      <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 mb-3">
        <Button
          onClick={handleEnableEdition}
          disabled={isEditing || isSaving}
        >
          {hasCoordinates ? "Editar datos camara" : "Completar datos camara"}
        </Button>
        {isEditing && (
          <>
            <Button
              variant="success"
              onClick={handleSave}
              disabled={!selectedPoint || isSaving}
            >
              Guardar datos camara
            </Button>
            <Button
              variant="danger"
              onClick={handleCancelEdition}
              disabled={isSaving}
            >
              Cancelar
            </Button>
          </>
        )}
        <label className="mb-0">
          {isEditing
            ? "Hace click en el mapa para marcar la ubicacion y completa la fecha si corresponde."
            : hasCoordinates
              ? "Mapa en modo solo lectura."
              : "La camara no tiene ubicacion cargada."}
        </label>
      </div>
      {isEditing && (
        <div className="mb-3 d-flex flex-column gap-3" style={{ maxWidth: "420px" }}>
          {/* <div>
            <label className="form-label" htmlFor="lugar_camara">
              Lugar
            </label>
            <input
              id="lugar_camara"
              type="text"
              className="form-control"
              value={lugar}
              onChange={(e) => setLugar(e.target.value)}
            />
          </div> */}
          <div>
            <label className="form-label" htmlFor="fecha_colocacion_camara">
              Fecha de colocacion
            </label>
            <input
              id="fecha_colocacion_camara"
              type="date"
              className="form-control"
              value={fechaColocacion}
              onChange={(e) => setFechaColocacion(e.target.value)}
            />
          </div>
        </div>
      )}
      {selectedPoint && (
        <div className="mb-2">
          <small>
            Latitud: {selectedPoint.latitud} | Longitud: {selectedPoint.longitud}
          </small>
        </div>
      )}
      <div id="mapaCamara" style={{ height: "200px" }}></div>
    </div>
  );
};

export default MapaCamara;
